import { useState, useEffect, useRef, useCallback } from 'react';
import logo from '../assets/logo.svg';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import { useVisualViewportHeight } from '../hooks/useVisualViewportHeight';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { clipboardApi } from '../api/clipboard';
import { toast } from 'react-hot-toast';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import FeatureButton from './common/FeatureButton';
import Tooltip from './common/Tooltip';
import ImageUploadButton from './ImageUploadButton';
import ImagePreview from './ImagePreview';
import ImageModal from './ImageModal';
import MarkdownRenderer from './MarkdownRenderer';
import StreamingMarkdownRenderer from './StreamingMarkdownRenderer';
import StreamingText from './StreamingText';
import UpgradePrompt from './UpgradePrompt';
import AIThinkingAnimation from './AIThinkingAnimation';
import BridgeTitleModal from './BridgeTitleModal';
import FileExportModal from './FileExportModal';
import { AI_DEFAULTS } from '../config';
import { getDenominationAbbreviation } from '../constants/denominations';

type FeatureMode = 'administrative' | 'bible' | 'doctrinal' | 'documentation' | 'research' | null;

export default function ChatView() {
  const {
    currentConversation,
    conversations,
    createConversation,
    loadConversation,
    loadConversations,
    addMessageToStore,
    updateVerbosity
  } = useChatStore();
  const { user } = useAuthStore();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState<FeatureMode>(null);
  const [isDetailMode, setIsDetailMode] = useState(false);
  const [showFeatureDropdown, setShowFeatureDropdown] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [showUrlWarning, setShowUrlWarning] = useState(true);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ current: number; limit: number } | null>(null);
  const [imageModalData, setImageModalData] = useState<{ url: string; filename: string } | null>(null);
  const [showBridgeTitleModal, setShowBridgeTitleModal] = useState(false);
  const [pendingBridgeData, setPendingBridgeData] = useState<{ content: string; role: 'user' | 'assistant' } | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportContent, setExportContent] = useState<{ content: string; title: string } | null>(null);
  const [pendingMessages, setPendingMessages] = useState<Set<string>>(new Set());
  const { isRecording, isTranscribing, isWarming, error: voiceError, audioLevel, waveformData, interimTranscript, startRecording, stopRecording } = useVoiceRecording();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const viewportHeight = useVisualViewportHeight();
  const isMobile = useIsMobile();
  const deviceInfo = useDeviceDetection();


  // Restore specialty agent when loading conversation with failed message
  useEffect(() => {
    if (!currentConversation?.messages || currentConversation.messages.length < 1) {
      return;
    }

    const messages = currentConversation.messages;
    const lastMessage = messages[messages.length - 1];

    // If last message is user message with a specialty agent, restore that agent
    // This handles cases where user reloads after error or resends failed message
    if (lastMessage.role === 'user' && lastMessage.feature_mode) {
      setActiveFeature(lastMessage.feature_mode as FeatureMode);
    }
  }, [currentConversation?.id, currentConversation?.messages?.length]);

  // Warn before leaving if there's an unsaved conversation with messages
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if current conversation has messages but might not be saved
      if (currentConversation?.messages && currentConversation.messages.length > 0) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return 'You have unsaved conversation data. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentConversation]);

  // Sync detail mode UI state with conversation's verbosity setting
  useEffect(() => {
    if (currentConversation?.response_verbosity) {
      setIsDetailMode(currentConversation.response_verbosity === 'detailed');
    } else {
      setIsDetailMode(false);
    }
  }, [currentConversation?.id, currentConversation?.response_verbosity]);

  // Clean clipboard copy — strip dark backgrounds, excessive margins, and theme colors
  const handleCleanCopy = (e: React.ClipboardEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

    try {
      const range = selection.getRangeAt(0);
      const fragment = range.cloneContents();
      const div = document.createElement('div');
      div.appendChild(fragment);

      // Classes to strip (dark theme visuals, spacing, borders)
      const stripClassPatterns = /\b(bg-\S+|text-dark-\S+|text-gray-\S+|text-white|mb-\S+|mt-\S+|leading-\S+|space-y-\S+|border-\S+|rounded-\S+|opacity-\S+|p-\d+|px-\S+|py-\S+|sm:p-\S+|md:p-\S+)\b/g;

      div.querySelectorAll('*').forEach(el => {
        if (el instanceof HTMLElement) {
          // Strip inline styles that carry dark theme visuals
          el.style.removeProperty('background-color');
          el.style.removeProperty('background');
          el.style.removeProperty('color');
          el.style.removeProperty('margin-top');
          el.style.removeProperty('margin-bottom');
          el.style.removeProperty('border');
          el.style.removeProperty('border-color');

          // Strip Tailwind utility classes that cause paste issues
          if (el.className && typeof el.className === 'string') {
            el.className = el.className.replace(stripClassPatterns, '').replace(/\s+/g, ' ').trim();
          }
        }
      });

      // Write cleaned HTML and plain text to clipboard
      const clipboardData = e.nativeEvent.clipboardData || (e as any).clipboardData;
      if (clipboardData) {
        clipboardData.setData('text/html', div.innerHTML);
        clipboardData.setData('text/plain', selection.toString());
        e.preventDefault();
      }
    } catch (err) {
      // If cleanup fails, let browser handle copy normally
      console.warn('Clean copy failed, falling back to default:', err);
    }
  };

  const handleNewChat = async () => {
    try {
      await createConversation('New Chat');
    } catch (error: any) {
      console.error('Failed to create new chat:', error);

      // Check if it's a limit error
      if (error?.response?.status === 403 && error?.response?.data?.detail?.error_code === 'CONVERSATION_LIMIT_REACHED') {
        const detail = error.response.data.detail;
        setLimitInfo({ current: detail.current, limit: detail.limit });
        setShowUpgradePrompt(true);
      } else {
        toast.error('Failed to create new chat');
      }
    }
  };

  const handleVoiceToggle = async () => {
    if (isRecording) {
      // Stop recording and transcribe
      const transcription = await stopRecording();
      if (transcription) {
        setInput(transcription);
        setActiveFeature(null);
      }
    } else {
      // Start recording
      await startRecording();
      // Voice recording state tracked by isRecording, not activeFeature
      setActiveFeature(null);
    }
  };

  // Show voice error if any
  useEffect(() => {
    if (voiceError) {
      console.error('Voice error:', voiceError);
      // Could show a toast notification here
    }
  }, [voiceError]);

  // Track recording duration
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      setRecordingDuration(0);
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  // Scroll to bottom helper
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior
      });
    }
  }, []);

  // Detect user manual scroll to disable auto-scroll
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    // Enable auto-scroll if user scrolls near bottom, disable if they scroll up
    setShouldAutoScroll(isNearBottom);
  }, []);

  // Auto-scroll when messages change (during streaming) if auto-scroll is enabled
  useEffect(() => {
    if (shouldAutoScroll && currentConversation?.messages) {
      scrollToBottom('smooth');
    }
  }, [currentConversation?.messages, shouldAutoScroll, scrollToBottom]);

  // Scroll to bottom when loading starts (user sends message)
  useEffect(() => {
    if (isLoading) {
      setShouldAutoScroll(true);
      scrollToBottom('smooth');
    }
  }, [isLoading, scrollToBottom]);

  // Auto-resize textarea as user types
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on content, with max of ~10 lines (approx 240px)
      const newHeight = Math.min(textarea.scrollHeight, 240);
      textarea.style.height = `${newHeight}px`;
    }
  }, [input]);

  const sendMessage = async (messageContent: string, images?: File[], imageUrls?: string[], documents?: File[], useStreaming: boolean = true) => {
    // Check if this message is already being sent (prevent race condition duplicates)
    if (pendingMessages.has(messageContent)) {
      return;
    }

    // Mark message as pending
    setPendingMessages(prev => new Set(prev).add(messageContent));

    // Create conversation if none exists
    let conversation = currentConversation;
    if (!conversation) {
      try {
        conversation = await createConversation('New Chat');
      } catch (error: any) {
        console.error('Failed to create conversation:', error);
        // Remove from pending on error
        setPendingMessages(prev => {
          const next = new Set(prev);
          next.delete(messageContent);
          return next;
        });

        // Check if it's a limit error
        if (error?.response?.status === 403 && error?.response?.data?.detail?.error_code === 'CONVERSATION_LIMIT_REACHED') {
          const detail = error.response.data.detail;
          setLimitInfo({ current: detail.current, limit: detail.limit });
          setShowUpgradePrompt(true);
        } else {
          toast.error('Failed to create conversation');
        }
        return;
      }
    }

    setIsLoading(true);

    // Create placeholder message for AI response (used for both streaming and errors)
    const aiMessageId = crypto.randomUUID();
    const aiPlaceholderMessage: any = {
      id: aiMessageId,
      conversation_id: conversation.id,
      role: 'assistant' as const,
      content: '',
      created_at: new Date().toISOString()
    };

    try {
      // Improved duplicate detection: check last USER message specifically (not assistant placeholder)
      const currentMessages = conversation?.messages || [];
      const userMessages = currentMessages.filter(m => m.role === 'user');
      const lastUserMessage = userMessages[userMessages.length - 1];
      const isDuplicateResend =
        lastUserMessage?.content === messageContent &&
        !pendingMessages.has(messageContent);

      // Only add user message if not a duplicate resend
      if (!isDuplicateResend) {
        // Include document filenames in the displayed message if any were attached
        let displayContent = messageContent;
        if (documents && documents.length > 0) {
          const docNames = documents.map(d => d.name).join(', ');
          displayContent = `${messageContent}\n\n📎 Attached: ${docNames}`;
        }
        const userMessage: any = {
          id: crypto.randomUUID(),
          conversation_id: conversation.id,
          role: 'user' as const,
          content: displayContent,
          created_at: new Date().toISOString(),
          feature_mode: activeFeature || undefined,
        };
        addMessageToStore(userMessage);
      }

      const token = localStorage.getItem('access_token');

      // Use streaming for text-only messages (images not supported in streaming yet)
      if (useStreaming && (!images || images.length === 0) && (!imageUrls || imageUrls.length === 0)) {
        // Add placeholder message for streaming content
        addMessageToStore(aiPlaceholderMessage);

        // Use fetch with streaming instead of EventSource for better control
        const formData = new FormData();
        formData.append('message', messageContent);
        formData.append('conversation_id', conversation.id);
        formData.append('max_tokens', String(AI_DEFAULTS.MAX_TOKENS));
        formData.append('temperature', String(AI_DEFAULTS.TEMPERATURE));
        if (activeFeature) {
          formData.append('feature_mode', activeFeature);
        }

        const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}/api/ai/chat/stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const status = response.status;
          if (status === 401) {
            throw new Error('Your session has expired. Please log in again.');
          } else if (status === 429) {
            throw new Error('You have reached your AI usage limit. Please try again later.');
          } else if (status >= 500) {
            throw new Error('The AI service is temporarily unavailable. Please try again in a moment.');
          }
          throw new Error(`Streaming request failed (${status})`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';
        let incompleteLineBuffer = '';
        let lastUpdateTime = Date.now();
        const UPDATE_INTERVAL = 500; // Update display every 500ms during streaming
        const STREAMING_TIMEOUT = 300000; // 5 minute timeout for reasoning models (gpt-5-mini uses internal reasoning tokens)

        // Add timeout to detect stalled streams
        let streamingTimeoutId: NodeJS.Timeout | null = null;
        let isStreamComplete = false;

        if (reader) {
          streamingTimeoutId = setTimeout(() => {
            if (!isStreamComplete) {
              console.error('[STREAMING] Timeout - no response in 5 minutes');
              reader.cancel(); // Cancel the stream
              const errorMessage = {
                ...aiPlaceholderMessage,
                content: 'Error: Response timeout. The AI took too long to respond. Please try again.'
              };
              addMessageToStore(errorMessage);
              setIsLoading(false);
            }
          }, STREAMING_TIMEOUT);

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                isStreamComplete = true;
                if (streamingTimeoutId) clearTimeout(streamingTimeoutId);
                break;
              }

            const chunk = decoder.decode(value, { stream: true });
            const fullChunk = incompleteLineBuffer + chunk;
            const messages = fullChunk.split('\n\n');
            incompleteLineBuffer = messages.pop() || '';

            for (const message of messages) {
              if (message.startsWith('data: ')) {
                const data = message.slice(6);
                const trimmedData = data.trim();

                if (!trimmedData) continue;

                try {
                  // Parse JSON-encoded SSE data
                  const parsed = JSON.parse(trimmedData);

                  if (parsed.type === 'done') {
                    // Stream complete - save final message
                    const finalMessage = {
                      ...aiPlaceholderMessage,
                      content: accumulatedContent
                    };
                    addMessageToStore(finalMessage);
                    await loadConversations();
                    break;
                  } else if (parsed.error) {
                    // Error occurred during streaming - update the streaming message with error
                    const errorMessage = {
                      ...aiPlaceholderMessage,
                      content: `Error: ${parsed.error}`
                    };
                    addMessageToStore(errorMessage);
                    return; // Exit streaming loop
                  } else if (parsed.content) {
                    // Accumulate content chunk (newlines preserved via JSON encoding)
                    accumulatedContent += parsed.content;

                    // Throttled update: only update display every UPDATE_INTERVAL ms
                    const now = Date.now();
                    if (now - lastUpdateTime >= UPDATE_INTERVAL) {
                      const updatedMessage = {
                        ...aiPlaceholderMessage,
                        content: accumulatedContent
                      };
                      addMessageToStore(updatedMessage);
                      lastUpdateTime = now;
                    }
                  }
                } catch (e) {
                  console.error('Failed to parse SSE data:', trimmedData, e);
                  // If parsing fails, might be old format - throw error
                  throw new Error('Invalid SSE format');
                }
              }
            }
            }
          } catch (streamError) {
            // Clear timeout on error
            isStreamComplete = true;
            if (streamingTimeoutId) clearTimeout(streamingTimeoutId);
            console.error('[STREAMING] Error during stream processing:', streamError);
            throw streamError; // Re-throw to be caught by outer catch
          } finally {
            // Always clear timeout when streaming ends
            if (streamingTimeoutId) clearTimeout(streamingTimeoutId);
          }
        }
      } else {
        // Non-streaming path (for images or when streaming disabled)
        let response;

        // Use multimodal endpoint if images or image URLs are provided
        if ((images && images.length > 0) || (imageUrls && imageUrls.length > 0)) {
          const formData = new FormData();
          formData.append('message', messageContent);
          formData.append('conversation_id', conversation.id);
          formData.append('max_tokens', String(AI_DEFAULTS.MAX_TOKENS));
          formData.append('temperature', String(AI_DEFAULTS.TEMPERATURE));
          if (activeFeature) {
            formData.append('feature_mode', activeFeature);
          }

          // Append all uploaded image files
          if (images) {
            images.forEach((image) => {
              formData.append('images', image);
            });
          }

          // Append all attached documents
          if (documents) {
            documents.forEach((doc) => {
              formData.append('documents', doc);
            });
          }

          // Append all image URLs
          if (imageUrls) {
            imageUrls.forEach((url) => {
              formData.append('image_urls', url);
            });
          }

          response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/ai/chat/multimodal`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          });
        } else {
          // Use regular chat endpoint
          response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/ai/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              message: messageContent,
              conversation_id: conversation.id,
              max_tokens: AI_DEFAULTS.MAX_TOKENS,
              temperature: AI_DEFAULTS.TEMPERATURE,
              feature_mode: activeFeature || null,
            }),
          });
        }

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail?.message || 'AI request failed');
        }

        const aiResponse = await response.json();

        // Add AI response to UI (backend already saved it to DB)
        const aiMessage: any = {
          id: crypto.randomUUID(),
          conversation_id: conversation.id,
          role: 'assistant' as const,
          content: aiResponse.content,
          model: aiResponse.model,
          provider: aiResponse.provider,
          created_at: new Date().toISOString(),
          feature_mode: activeFeature || undefined,
        };

        addMessageToStore(aiMessage);

        // Reload conversation to get actual messages with attachments from backend
        if (conversation?.id && images && images.length > 0) {
          await loadConversation(conversation.id);
        }
      }
    } catch (error: any) {
      console.error('Error:', error);

      // Update the existing placeholder message with error content
      const errorMessage: any = {
        ...aiPlaceholderMessage,
        content: `Error: ${error.message || 'Failed to get AI response. Please check your API keys in settings.'}`,
      };
      addMessageToStore(errorMessage);
    } finally {
      setIsLoading(false);
      // Always remove message from pending set when done (success or error)
      setPendingMessages(prev => {
        const next = new Set(prev);
        next.delete(messageContent);
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userInput = input;
    const images = selectedImages.length > 0 ? selectedImages : undefined;
    const urls = imageUrls.length > 0 ? imageUrls : undefined;
    const documents = selectedDocuments.length > 0 ? selectedDocuments : undefined;
    setInput('');
    setSelectedImages([]);
    setImageUrls([]);
    setSelectedDocuments([]);

    await sendMessage(userInput, images, urls, documents);
  };

  const handleImageRemove = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleUrlRemove = (index: number) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const isValidImageUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      // Check if it's http or https
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }
      // Check if it has a likely image extension
      const path = urlObj.pathname.toLowerCase();
      return path.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i) !== null;
    } catch {
      return false;
    }
  };

  const extractImageUrls = (text: string): string[] => {
    // Regex to find URLs in text
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = text.match(urlRegex) || [];

    // Filter to only valid image URLs
    const imageUrls = matches.filter(url => isValidImageUrl(url));

    // Remove duplicates
    return Array.from(new Set(imageUrls));
  };

  // Auto-detect image URLs when input changes
  const handleInputChange = (newInput: string) => {
    setInput(newInput);

    // Extract image URLs from the text
    const detectedUrls = extractImageUrls(newInput);

    // Update imageUrls if different
    if (JSON.stringify(detectedUrls) !== JSON.stringify(imageUrls)) {
      setImageUrls(detectedUrls);

      // Show security warning if URLs detected and warning hasn't been dismissed
      if (detectedUrls.length > 0 && showUrlWarning) {
        // Warning will be shown in UI
      }
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const imageFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    // Add pasted images to selected images
    if (imageFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...imageFiles]);
      e.preventDefault(); // Prevent default paste behavior for images
    }
  };

  const handleResend = (messageContent: string) => {
    // Load the message content into the input field for editing
    setInput(messageContent);

    // Scroll to bottom to show the input field
    setTimeout(() => {
      const inputElement = document.querySelector('textarea');
      if (inputElement) {
        inputElement.focus();
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleSendMessageToClipboard = (messageContent: string, messageRole: 'user' | 'assistant') => {
    // Store the pending data and show the modal
    setPendingBridgeData({ content: messageContent, role: messageRole });
    setShowBridgeTitleModal(true);
  };

  const handleExportMessage = (messageContent: string, messageRole: 'user' | 'assistant') => {
    // Generate a default title from the first line of content
    const defaultTitle = messageContent.split('\n')[0]
      .replace(/^#+\s*/, '') // Remove markdown headers
      .substring(0, 60)
      .trim() || `${messageRole === 'user' ? 'User' : 'Assistant'} Message`;

    setExportContent({ content: messageContent, title: defaultTitle });
    setShowExportModal(true);
  };

  const handleBridgeTitleModalSave = async (customTitle: string) => {
    if (!pendingBridgeData) return;

    try {
      // Create clipboard item from individual message with custom title
      await clipboardApi.create({
        content_variants: {
          plain: pendingBridgeData.content.replace(/[#*_~`>\[\]()!|-]/g, '').replace(/\n{3,}/g, '\n\n').trim(),
          markdown: pendingBridgeData.content,
        },
        title: customTitle,
        content_type: 'text',
        tags: ['chat', pendingBridgeData.role, currentConversation?.title || 'conversation'],
      });

      toast.success('Message saved to clipboard!');
      setShowBridgeTitleModal(false);
      setPendingBridgeData(null);
    } catch (error: any) {
      console.error('Error sending message to clipboard:', error);

      // Check if it's a limit error
      if (error?.response?.status === 403 && error?.response?.data?.detail?.error_code === 'CLIPBOARD_LIMIT_REACHED') {
        const detail = error.response.data.detail;
        setLimitInfo({ current: detail.current, limit: detail.limit });
        setShowUpgradePrompt(true);
        setShowBridgeTitleModal(false);
        setPendingBridgeData(null);
      } else {
        toast.error('Failed to save to clipboard');
      }
    }
  };

  const handleBridgeTitleModalCancel = () => {
    setShowBridgeTitleModal(false);
    setPendingBridgeData(null);
  };

  const handleTextareaFocus = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Delay to allow keyboard animation
    setTimeout(() => {
      e.target.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }, 300);
  }, []);

  // Handle scroll indicators for feature buttons
  useEffect(() => {
    const scrollContainer = document.querySelector('.snap-scroll-x');
    const leftIndicator = document.getElementById('scroll-left-indicator');
    const rightIndicator = document.getElementById('scroll-right-indicator');

    if (!scrollContainer || !leftIndicator || !rightIndicator) return;

    const updateIndicators = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer as HTMLElement;

      // Show left indicator if scrolled right
      if (scrollLeft > 10) {
        leftIndicator.style.opacity = '1';
      } else {
        leftIndicator.style.opacity = '0';
      }

      // Show right indicator if not at end
      if (scrollLeft < scrollWidth - clientWidth - 10) {
        rightIndicator.style.opacity = '1';
      } else {
        rightIndicator.style.opacity = '0';
      }
    };

    // Initial check
    updateIndicators();

    // Listen for scroll events
    scrollContainer.addEventListener('scroll', updateIndicators);
    window.addEventListener('resize', updateIndicators);

    return () => {
      scrollContainer.removeEventListener('scroll', updateIndicators);
      window.removeEventListener('resize', updateIndicators);
    };
  }, []);

  return (
    <div
      className="flex-1 flex flex-col max-w-full overflow-x-hidden"
      style={{ height: viewportHeight > 0 ? `${viewportHeight}px` : '100vh' }}
    >
      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        onCopy={handleCleanCopy}
        className="flex-1 overflow-y-auto"
      >
        {!currentConversation ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <div className="mb-8">
              <img src={logo} alt="PARACLETE" className="h-20 w-auto mx-auto opacity-50" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Welcome to PARACLETE</h2>
            <p className="text-dark-text-muted max-w-md mb-6">
              AI-Powered Clipboard Bridge for Christian Research
            </p>
            <button
              onClick={handleNewChat}
              className="px-6 py-3 bg-accent-blue hover:bg-accent-blue/80 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Start New Chat
            </button>
            <p className="text-dark-text-muted text-sm mt-6">
              Or select a conversation from the sidebar
            </p>
          </div>
        ) : !currentConversation.messages || currentConversation.messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <div className="mb-8">
              <img src={logo} alt="PARACLETE" className="h-20 w-auto mx-auto opacity-50" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Start Your Conversation</h2>
            <p className="text-dark-text-muted max-w-md">
              Type your message below to begin
            </p>
          </div>
        ) : (
          <div className="space-y-6 p-3 sm:p-4 text-sm sm:text-base">
            {currentConversation.messages.map((message) => {
              // Detect doctrinal warning in message content
              const hasDoctrinalWarning = message.role === 'assistant' && (
                message.content.includes('🚨 CRITICAL DOCTRINAL CONFLICT') ||
                message.content.includes('⚠️ DOCTRINAL WARNING') ||
                message.content.includes('⚠️ DOCTRINAL NOTICE')
              );

              return (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`group relative max-w-full ${
                      message.role === 'user'
                        ? 'w-[75%] sm:w-[80%] md:w-[75%] lg:w-[70%] rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl'
                        : 'w-full rounded-tr-2xl rounded-br-2xl rounded-bl-2xl'
                    } p-3 sm:p-5 md:p-4 ${
                      message.role === 'user'
                        ? 'bg-accent-blue text-white'
                        : hasDoctrinalWarning
                        ? 'bg-red-500/10 border-2 border-red-500/50'
                        : 'bg-dark-card border border-dark-border'
                    }`}
                  >
                    {/* Action buttons container - top right of message */}
                    <div className={`absolute top-2 z-10 flex gap-1 ${message.role === 'user' ? 'right-2' : 'right-2'} md:opacity-0 md:group-hover:opacity-100 transition-opacity`}>
                      {/* Save to Bridge button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendMessageToClipboard(message.content, message.role);
                        }}
                        disabled={isLoading}
                        className={`min-w-[40px] min-h-[40px] flex items-center justify-center ${message.role === 'user' ? 'bg-white/10 hover:bg-white/20 active:bg-white/30' : 'bg-accent-blue/20 hover:bg-accent-blue/30 active:bg-accent-blue/40'} rounded-md disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Save to Bridge"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-4 h-4 ${message.role === 'user' ? 'text-white' : ''}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                        </svg>
                      </button>

                      {/* Export to File button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportMessage(message.content, message.role);
                        }}
                        disabled={isLoading}
                        className={`min-w-[40px] min-h-[40px] flex items-center justify-center ${message.role === 'user' ? 'bg-white/10 hover:bg-white/20 active:bg-white/30' : 'bg-accent-orange/20 hover:bg-accent-orange/30 active:bg-accent-orange/40'} rounded-md disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Export to File (DOCX, XLSX, etc.)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-4 h-4 ${message.role === 'user' ? 'text-white' : 'text-accent-orange'}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                      </button>

                      {/* Resend button - only for user messages */}
                      {message.role === 'user' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResend(message.content);
                          }}
                          disabled={isLoading}
                          className="min-w-[40px] min-h-[40px] flex items-center justify-center bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit and resend"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-white">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="text-sm mb-1 opacity-70 pr-24">
                      {message.role === 'user' ? 'You' : (
                        <>
                          Virtual Assistant
                          {user?.denomination && (
                            <span className="ml-2 px-2 py-0.5 bg-accent-blue/20 text-accent-blue text-xs rounded-full font-medium">
                              {user.denomination.toLowerCase() === 'non_denominational' || user.denomination.toLowerCase() === 'non-denominational'
                                ? 'NON-DENOMINATIONAL'
                                : `${getDenominationAbbreviation(user.denomination)} DOCTRINE`
                              }
                            </span>
                          )}
                        </>
                      )}
                      {message.feature_mode && (
                        <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30 font-medium">
                          {message.feature_mode === 'administrative' ? 'Administrative Agent' :
                           message.feature_mode === 'bible' ? 'Bible References Agent' :
                           message.feature_mode === 'doctrinal' ? 'Doctrinal Agent' :
                           message.feature_mode === 'documentation' ? 'Biblical Documentation Agent' :
                           message.feature_mode === 'research' ? 'Deep Research Agent' :
                           message.feature_mode}
                        </span>
                      )}
                      {hasDoctrinalWarning && (
                        <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          Doctrinal Warning
                        </span>
                      )}
                    </div>

                    {/* Display image attachments with larger thumbnails */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {message.attachments.map((attachment) => (
                          <div key={attachment.id} className="relative group">
                            <img
                              src={`data:${attachment.mime_type};base64,${attachment.data_base64}`}
                              alt={attachment.filename}
                              loading="lazy"
                              className="rounded-lg max-w-[200px] sm:max-w-xs max-h-48 sm:max-h-64 object-contain border border-dark-border hover:border-accent-blue transition-colors cursor-pointer"
                              onClick={() => {
                                setImageModalData({
                                  url: `data:${attachment.mime_type};base64,${attachment.data_base64}`,
                                  filename: attachment.filename
                                });
                              }}
                              title="Click to view full size"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-lg md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                              {attachment.filename}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {message.role === 'assistant' ? (
                      message.content ? (
                        <StreamingMarkdownRenderer
                          content={message.content}
                          isStreaming={isLoading}
                          conversationTitle={currentConversation?.title}
                          onExportTable={(tableContent, tableTitle) => {
                            setExportContent({ content: tableContent, title: tableTitle });
                            setShowExportModal(true);
                          }}
                        />
                      ) : (
                        <AIThinkingAnimation />
                      )
                    ) : (
                      <StreamingText content={message.content} speed={5} />
                    )}
                    <div className="text-xs mt-2 opacity-50">
                      {new Date(message.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-dark-border p-3 sm:p-4 bg-dark-bg" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto text-sm sm:text-base">
          {/* Feature Buttons */}
          <div className="relative mb-3 overflow-y-visible">
            {/* Left scroll indicator gradient */}
            <div className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-dark-bg to-transparent pointer-events-none z-10 opacity-0 transition-opacity" id="scroll-left-indicator"></div>

            {/* Right scroll indicator gradient */}
            <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-dark-bg to-transparent pointer-events-none z-10 opacity-100 transition-opacity" id="scroll-right-indicator"></div>

            {/* Desktop: Horizontal scrolling buttons */}
            <div className="hidden sm:flex w-full overflow-x-auto overflow-y-visible gap-2 pt-2 pb-2 pl-1 scrollbar-hide snap-scroll-x" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* Detail Toggle */}
              <Tooltip content={isDetailMode ? "Detail mode ON - Getting comprehensive answers. Click to switch to standard mode." : "Detail mode OFF - Getting direct, factual answers. Click for comprehensive responses."} position="bottom">
                <button
                  type="button"
                  onClick={async () => {
                    const newMode = !isDetailMode;

                    // Create conversation if it doesn't exist
                    let conversation = currentConversation;
                    if (!conversation) {
                      try {
                        conversation = await createConversation('New Chat');
                      } catch (error) {
                        console.error('Failed to create conversation:', error);
                        toast.error('Failed to create conversation');
                        return;
                      }
                    }

                    // Optimistically update UI
                    setIsDetailMode(newMode);
                    const newVerbosity = newMode ? 'detailed' : 'concise';

                    try {
                      await updateVerbosity(conversation.id, newVerbosity);
                      toast.success(`${newMode ? 'Detailed' : 'Concise'} mode activated`);
                    } catch (error) {
                      console.error('Failed to update verbosity:', error);
                      toast.error('Failed to update response mode. Please try again.');
                      // Rollback UI state on failure
                      setIsDetailMode(!newMode);
                    }
                  }}
                  aria-pressed={isDetailMode}
                  className={`
                    px-3 py-2.5 rounded-lg min-h-[44px] flex-shrink-0
                    text-xs sm:text-sm font-medium whitespace-nowrap
                    transition-all duration-200 active:scale-95
                    focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 focus:ring-offset-dark-bg
                    ${isDetailMode
                      ? 'bg-orange-700 text-white hover:bg-orange-800'
                      : 'bg-dark-card text-dark-text-muted border border-dark-border hover:border-orange-700/50 hover:text-accent-orange'
                    }
                  `}
                >
                  Detail
                </button>
              </Tooltip>

              <Tooltip content="Specialty agent to assist in research and preparation of business documents for church operations and administration" position="bottom">
                <FeatureButton
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" /></svg>}
                  label="Administrative"
                  isActive={activeFeature === 'administrative'}
                  onClick={() => setActiveFeature(activeFeature === 'administrative' ? null : 'administrative')}
                />
              </Tooltip>
              <Tooltip content="Specialty agent to assist with scripture analysis, cross-references, parallel passages, and historical context" position="bottom">
                <FeatureButton
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>}
                  label="Bible References"
                  isActive={activeFeature === 'bible'}
                  onClick={() => setActiveFeature(activeFeature === 'bible' ? null : 'bible')}
                />
              </Tooltip>
              <Tooltip content="Specialty agent to assist in validating content against your denominational doctrine and theological positions" position="bottom">
                <FeatureButton
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" /></svg>}
                  label="Doctrinal"
                  isActive={activeFeature === 'doctrinal'}
                  onClick={() => setActiveFeature(activeFeature === 'doctrinal' ? null : 'doctrinal')}
                />
              </Tooltip>
              <Tooltip content="Specialty agent to assist in preparing documents for church services and ministerial duties" position="bottom">
                <FeatureButton
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>}
                  label="Biblical Documentation"
                  isActive={activeFeature === 'documentation'}
                  onClick={() => setActiveFeature(activeFeature === 'documentation' ? null : 'documentation')}
                />
              </Tooltip>
              <Tooltip content="Specialty agent to assist with in-depth biblical and theological research" position="bottom">
                <FeatureButton
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>}
                  label="Deep Research"
                  isActive={activeFeature === 'research'}
                  onClick={() => setActiveFeature(activeFeature === 'research' ? null : 'research')}
                />
              </Tooltip>
            </div>

            {/* Mobile: Detail button + Dropdown for features */}
            <div className="flex sm:hidden gap-2 pt-2 pb-2 pl-1">
              {/* Detail Toggle */}
              <button
                type="button"
                onClick={async () => {
                  const newMode = !isDetailMode;

                  // Create conversation if it doesn't exist
                  let conversation = currentConversation;
                  if (!conversation) {
                    try {
                      conversation = await createConversation('New Chat');
                    } catch (error) {
                      console.error('Failed to create conversation:', error);
                      toast.error('Failed to create conversation');
                      return;
                    }
                  }

                  // Optimistically update UI
                  setIsDetailMode(newMode);
                  const newVerbosity = newMode ? 'detailed' : 'concise';

                  try {
                    await updateVerbosity(conversation.id, newVerbosity);
                    toast.success(`${newMode ? 'Detailed' : 'Concise'} mode activated`);
                  } catch (error) {
                    console.error('Failed to update verbosity:', error);
                    toast.error('Failed to update response mode. Please try again.');
                    // Rollback UI state on failure
                    setIsDetailMode(!newMode);
                  }
                }}
                aria-pressed={isDetailMode}
                className={`
                  px-3 py-2.5 rounded-lg min-h-[44px] flex-shrink-0
                  text-xs font-medium whitespace-nowrap
                  transition-all duration-200 active:scale-95
                  focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 focus:ring-offset-dark-bg
                  ${isDetailMode
                    ? 'bg-orange-700 text-white hover:bg-orange-800'
                    : 'bg-dark-card text-dark-text-muted border border-dark-border hover:border-orange-700/50 hover:text-accent-orange'
                  }
                `}
              >
                Detail
              </button>

              {/* Features Dropdown */}
              <div className="relative flex-1">
                <button
                  type="button"
                  onClick={() => setShowFeatureDropdown(!showFeatureDropdown)}
                  className={`
                    w-full px-3 py-2.5 rounded-lg min-h-[44px] flex items-center justify-between gap-2
                    text-xs font-medium transition-all duration-200 active:scale-95
                    ${activeFeature
                      ? 'bg-gradient-to-r from-accent-blue to-accent-orange text-white'
                      : 'bg-dark-card border border-dark-border text-dark-text-muted'
                    }
                  `}
                >
                  <span>{activeFeature ? (
                    activeFeature === 'administrative' ? 'Administrative' :
                    activeFeature === 'bible' ? 'Bible' :
                    activeFeature === 'doctrinal' ? 'Doctrine' :
                    activeFeature === 'documentation' ? 'Docs' :
                    'Research'
                  ) : 'Features'}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-4 h-4 transition-transform ${showFeatureDropdown ? 'rotate-180' : ''}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showFeatureDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-dark-card border border-dark-border rounded-lg shadow-xl z-50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveFeature(activeFeature === 'administrative' ? null : 'administrative');
                        setShowFeatureDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                        activeFeature === 'administrative'
                          ? 'bg-accent-blue/20 text-accent-blue'
                          : 'text-dark-text hover:bg-dark-border'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                      </svg>
                      Administrative
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveFeature(activeFeature === 'bible' ? null : 'bible');
                        setShowFeatureDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                        activeFeature === 'bible'
                          ? 'bg-accent-blue/20 text-accent-blue'
                          : 'text-dark-text hover:bg-dark-border'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                      </svg>
                      Bible References
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveFeature(activeFeature === 'doctrinal' ? null : 'doctrinal');
                        setShowFeatureDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                        activeFeature === 'doctrinal'
                          ? 'bg-accent-blue/20 text-accent-blue'
                          : 'text-dark-text hover:bg-dark-border'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                      </svg>
                      Doctrinal
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveFeature(activeFeature === 'documentation' ? null : 'documentation');
                        setShowFeatureDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                        activeFeature === 'documentation'
                          ? 'bg-accent-blue/20 text-accent-blue'
                          : 'text-dark-text hover:bg-dark-border'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                      Biblical Documentation
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveFeature(activeFeature === 'research' ? null : 'research');
                        setShowFeatureDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                        activeFeature === 'research'
                          ? 'bg-accent-blue/20 text-accent-blue'
                          : 'text-dark-text hover:bg-dark-border'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                      </svg>
                      Deep Research
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recording Indicator */}
          {(isWarming || isRecording || isTranscribing) && (
            <div className="mb-3 p-4 bg-dark-card border border-accent-blue rounded-xl">
              {/* Warming Up State */}
              {isWarming && (
                <div className="flex items-center gap-3 justify-center py-2">
                  <div className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                  <div className="flex flex-col">
                    <span className="text-dark-text font-medium text-lg">Warming up microphone...</span>
                    <span className="text-dark-text-muted text-sm">Please wait, then start speaking</span>
                  </div>
                </div>
              )}

              {/* Recording State */}
              {isRecording && !isWarming && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 min-w-[120px]">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-dark-text font-medium">Recording</span>
                  </div>

                  {/* Waveform Visualization */}
                  <div className="flex-1 flex items-center justify-center gap-[2px] h-16 px-4">
                    {waveformData.map((value, i) => {
                      // Show fewer bars on mobile (32 vs 64)
                      if (isMobile && i % 2 !== 0) return null;

                      // Create waveform effect - bars grow from center
                      const height = Math.max(2, (value / 100) * 64);
                      // Color based on intensity: quiet = blue, medium = green, loud = yellow/red
                      const getColor = (val: number) => {
                        if (val < 20) return 'bg-blue-500';
                        if (val < 50) return 'bg-green-500';
                        if (val < 75) return 'bg-yellow-500';
                        return 'bg-red-500';
                      };

                      return (
                        <div
                          key={i}
                          className={`w-[2px] rounded-full transition-all duration-75 ${getColor(value)}`}
                          style={{
                            height: `${height}px`,
                            opacity: value > 1 ? 0.9 : 0.3
                          }}
                        ></div>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-3 min-w-[100px] justify-end">
                    <span className="text-dark-text-muted text-sm font-mono">
                      {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                </div>
              )}

              {/* Transcribing State */}
              {isTranscribing && (
                <div className="flex items-center gap-3 justify-center py-2">
                  <div className="w-5 h-5 border-2 border-accent-blue border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-dark-text font-medium">Transcribing your speech...</span>
                </div>
              )}
            </div>
          )}

          {/* Interim Transcript (Web Speech API) */}
          {isRecording && interimTranscript && (
            <div className="mb-2 px-4 py-2 bg-dark-bg/50 border border-accent-blue/30 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent-blue rounded-full animate-pulse"></div>
                <span className="text-gray-400 text-sm italic">Listening: {interimTranscript}</span>
              </div>
            </div>
          )}

          {/* Image Previews */}
          {selectedImages.length > 0 && (
            <div className="mb-2">
              <ImagePreview files={selectedImages} onRemove={handleImageRemove} />
            </div>
          )}

          {/* Image URL Previews */}
          {imageUrls.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {imageUrls.map((url, index) => (
                <div key={index} className="flex items-center gap-2 bg-dark-card border border-dark-border rounded-lg px-3 py-2 max-w-md">
                  <img
                    src={url}
                    alt="URL Preview"
                    className="w-8 h-8 object-cover rounded"
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="gray"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                    }}
                  />
                  <span className="text-sm text-dark-text truncate max-w-[200px]" title={url}>
                    {url}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUrlRemove(index)}
                    className="text-dark-text-muted hover:text-red-500 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Security Warning (shown when URLs detected) */}
          {showUrlWarning && imageUrls.length > 0 && (
            <div className="mb-2 bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-yellow-200">
                    <strong>Security Notice:</strong> Image URLs detected in your message. These will be fetched by our servers. Do not include URLs containing sensitive data, authentication tokens, or private content.
                  </p>
                  <button
                    onClick={() => setShowUrlWarning(false)}
                    className="mt-2 text-xs text-yellow-400 hover:text-yellow-300 underline"
                  >
                    I understand, don't show this again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Document Previews */}
          {selectedDocuments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {selectedDocuments.map((doc, index) => (
                <div key={index} className="flex items-center gap-2 bg-dark-bg border border-dark-border rounded-lg px-3 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-accent-blue">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  <span className="text-sm text-dark-text truncate max-w-[150px]">{doc.name}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedDocuments(prev => prev.filter((_, i) => i !== index))}
                    className="text-dark-text-muted hover:text-red-500 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input Field */}
          <div className="flex gap-3 bg-dark-card border border-dark-border rounded-xl p-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              onPaste={handlePaste}
              onFocus={handleTextareaFocus}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder={
                activeFeature === 'administrative' ? "Ask about church administration, forms, letters, documents..." :
                activeFeature === 'bible' ? "Ask about Bible references..." :
                isRecording ? "Recording... click mic to stop" :
                activeFeature === 'doctrinal' ? "Ask about doctrinal positions..." :
                activeFeature === 'documentation' ? "What ministry document can I help you prepare?" :
                activeFeature === 'research' ? "What would you like to research?" :
                "Start typing to begin a new conversation..."
              }
              className="flex-1 bg-transparent px-2 py-2 text-dark-text placeholder-dark-text-muted focus:outline-none resize-none overflow-y-auto"
              rows={1}
              disabled={isLoading}
              style={{ minHeight: '44px' }}
            />
            <div className="flex items-end gap-2">
              <Tooltip content="Take photo or choose from gallery">
                <ImageUploadButton
                  onImagesSelected={(files) => setSelectedImages(prev => [...prev, ...files])}
                  disabled={isLoading}
                />
              </Tooltip>
              {/* Document upload button */}
              <Tooltip content="Attach documents (PDF, Word, Excel, etc.)">
                <label className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                  isLoading
                    ? 'opacity-50 cursor-not-allowed text-dark-text-muted'
                    : 'text-dark-text-muted hover:text-dark-text hover:bg-dark-bg active:bg-dark-card'
                }`}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls"
                    multiple
                    disabled={isLoading}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setSelectedDocuments(prev => [...prev, ...files]);
                        toast.success(`${files.length} document${files.length > 1 ? 's' : ''} selected`);
                      }
                      e.target.value = ''; // Reset input
                    }}
                    className="hidden"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                </label>
              </Tooltip>
              {/* Hide whisper microphone on mobile (iOS/Android) and macOS - they have superior native speech-to-text */}
              {!deviceInfo.shouldUseNativeSpeech && (
                <Tooltip content="Record voice message using Whisper AI">
                  <button
                    type="button"
                    onClick={handleVoiceToggle}
                    disabled={isTranscribing}
                    className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-all relative ${
                      isRecording
                        ? 'bg-red-500 text-white animate-pulse'
                        : isTranscribing
                        ? 'bg-accent-blue text-white opacity-50'
                        : 'text-dark-text-muted hover:text-dark-text hover:bg-dark-bg active:bg-dark-card'
                    }`}
                    title={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Voice input'}
                  >
                    {isTranscribing ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                        />
                      </svg>
                    )}
                  </button>
                </Tooltip>
              )}
              <Tooltip content="Send message (Enter)">
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center bg-gradient-to-r from-accent-blue to-accent-orange text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                    />
                  </svg>
                </button>
              </Tooltip>
            </div>
          </div>
        </form>
      </div>

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && limitInfo && (
        <UpgradePrompt
          isOpen={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          limitType="conversation"
          currentCount={limitInfo.current}
          limit={limitInfo.limit}
        />
      )}

      {/* Image Modal */}
      {imageModalData && (
        <ImageModal
          imageUrl={imageModalData.url}
          filename={imageModalData.filename}
          onClose={() => setImageModalData(null)}
        />
      )}

      {/* Bridge Title Modal */}
      <BridgeTitleModal
        isOpen={showBridgeTitleModal}
        defaultTitle={pendingBridgeData ?
          (pendingBridgeData.content.split('\n')[0].replace(/^#+\s*/, '').substring(0, 60).trim() ||
           `${pendingBridgeData.role === 'user' ? 'User' : 'Assistant'} Message`)
          : ''}
        messageContent={pendingBridgeData?.content || ''}
        onSave={handleBridgeTitleModalSave}
        onCancel={handleBridgeTitleModalCancel}
      />

      {/* File Export Modal */}
      <FileExportModal
        isOpen={showExportModal}
        content={exportContent?.content || ''}
        defaultTitle={exportContent?.title}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
}
